/**
 * Game numbers.
 *
 * Scope is per schedule version, per division, per game type — so 17u Pool
 * Game 7 and 16u Pool Game 7 both exist, and regenerating one division never
 * shifts another division's numbers. The database owns assignment (a BEFORE
 * INSERT trigger on scheduled_games) and uniqueness (a partial unique index);
 * this file only decides how a number is said out loud.
 */

export type GameNumberish = {
  game_type?: string | null;
  game_number?: number | null;
  round_number?: number | null;
  bracket_round?: number | null;
};

const TYPE_LABEL: Record<string, string> = {
  pool: "Pool Game",
  bracket: "Bracket Game",
  consolation: "Consolation Game",
  crossover: "Crossover Game",
};

export function gameTypeLabel(gameType?: string | null): string {
  return TYPE_LABEL[String(gameType ?? "pool")] ?? "Game";
}

/** "Pool Game 7", "Round 2 · Bracket Game 3". Empty string when unnumbered. */
export function formatGameNumber(game: GameNumberish, opts?: { withRound?: boolean }): string {
  const n = game?.game_number;
  if (n == null) return "";
  const label = `${gameTypeLabel(game.game_type)} ${n}`;
  const round = game.round_number ?? game.bracket_round ?? null;
  if (opts?.withRound !== false && round != null) return `Round ${round} · ${label}`;
  return label;
}

/**
 * How a game is named to a human, everywhere: the number first, because that
 * is what a director scans for, then the words so the number means something.
 *   "Game 18 · Round 1", "16u Game 30 · Final"
 * Mirrors gameName() in supabase/functions/_shared/bracket-slots.ts, so the
 * number on a card, in a dropdown and in a refusal message all read the same.
 */
export function gameName(
  g: { game_number?: number | null; bracket_label?: string | null; division?: string | null } | null | undefined,
  opts: { withDivision?: boolean } = {},
): string {
  const div = opts.withDivision && g?.division ? `${String(g.division).trim()} ` : '';
  const words = String(g?.bracket_label ?? '').trim();
  const n = g?.game_number;
  if (n == null) return `${div}${words || 'a game'}`.trim();
  return `${div}Game ${n}${words ? ` · ${words}` : ''}`;
}

/** Compact form for tight spaces: "#7". */
export function shortGameNumber(game: GameNumberish): string {
  return game?.game_number == null ? "" : `#${game.game_number}`;
}
